import { useQuery } from "@tanstack/react-query";
import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import { useEffect, useId, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { meQuery } from "../api/queries";
import { FirstDevice } from "../features/devices/FirstDevice";
import { maybeConfig } from "../config";
import { UserMenu } from "../features/account/UserMenu";
import { LanguageSwitch } from "../features/account/LanguageSwitch";
import { AlarmsBadge } from "../features/alarms/AlarmsBadge";
import { HelpBadge } from "../features/support/Requests";
import { OrgProvider, useOrg } from "../features/org/OrgProvider";
import { OrgSwitcher } from "../features/org/OrgSwitcher";
import { chosenLanguage, isLanguage } from "../i18n";
import { LiveProvider } from "../live/LiveProvider";
import { showServers } from "../servers/servers";
import { ErrorBoundary } from "../ui/ErrorBoundary";
import { ErrorMessage } from "../ui/ErrorMessage";
import { Logo } from "../ui/Logo";
import { useNarrow } from "../ui/narrow";
import { NetworkBanner } from "../ui/NetworkBanner";
import { MapStyleProvider } from "../widgets/map/style";
import { Loading } from "../ui/Spinner";
import { MAP_SCREEN } from "../features/map/ready";
import { hasOrganisationSection } from "../features/organisation/roles";
import { hasPartnerPages } from "../features/partners/partners";
import { serverRoleQuery } from "../ui/ServerRole";

/**
 * Top bar, skip link and main area, shared by the customer app and the console (WEB-F-SH-03). With `phone` (the
 * customer app, web-on-phones WOP-01, WOP-02) the frame works from 360 px: below 768 px the side navigation moves
 * into a full-height drawer behind a menu button in the top bar. The console stays desktop-first (768 px and up).
 */
export function Frame({
  brand,
  home = "/",
  actions,
  nav,
  phone = false,
  children,
}: {
  brand: string;
  home?: "/" | "/ops";
  actions: ReactNode;
  nav?: ReactNode;
  phone?: boolean;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // The drawer belongs to the page it was opened on, so following one of its links closes it.
  const [menuAt, setMenuAt] = useState<string | null>(null);
  // Only while the screen is narrow: a phone turned to landscape past 768 px drops the drawer, so the page it covered
  // is not left inert behind a hidden modal.
  const narrow = useNarrow();
  const menu = phone && narrow && nav !== undefined && menuAt === pathname;
  const drawerId = useId();
  return (
    <div className={`flex h-full flex-col ${phone ? "min-w-0" : "min-w-[768px]"}`}>
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded-md focus:bg-white focus:px-3 focus:py-2"
      >
        {t("app.skipToContent")}
      </a>
      <header
        className={`flex h-14 shrink-0 items-center border-b border-gray-200 bg-white ${phone ? "gap-2 px-2 md:gap-4 md:px-4" : "gap-4 px-4"}`}
      >
        {phone && nav !== undefined && (
          <button
            type="button"
            aria-expanded={menu}
            aria-controls={drawerId}
            onClick={() => setMenuAt(pathname)}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-gray-800 hover:bg-gray-100 md:hidden"
          >
            <MenuIcon />
            <span className="sr-only">{t("nav.open")}</span>
          </button>
        )}
        <Link to={home} className="flex shrink-0 items-center gap-2">
          <Logo size={32} />
          <span className={`font-semibold text-brand ${phone ? "sr-only md:not-sr-only" : ""}`}>{brand}</span>
        </Link>
        <div className={`flex min-w-0 flex-1 items-center justify-end ${phone ? "gap-2 md:gap-3" : "gap-3"}`}>
          {actions}
        </div>
      </header>
      <NetworkBanner />
      <div className="flex min-h-0 flex-1">
        {phone ? nav !== undefined && <div className="hidden md:flex">{nav}</div> : nav}
        {/* relative: absolutely placed content (screen-reader text, tooltips) stays inside the scrolling area instead
            of making the window itself scroll into blank space (owner's report #10). */}
        <main id="main" tabIndex={-1} className="relative min-w-0 flex-1 overflow-auto bg-gray-50">
          <ErrorBoundary resetKey={pathname}>{children}</ErrorBoundary>
        </main>
      </div>
      {menu && (
        <dialog
          id={drawerId}
          data-drawer=""
          aria-label={t("nav.label")}
          ref={(el) => {
            if (el && !el.open) el.showModal();
          }}
          onClose={() => setMenuAt(null)}
          onClick={(e) => {
            const target = e.target as HTMLElement;
            // A link followed, or a tap on the backdrop (the dialog itself), closes the drawer.
            if (target === e.currentTarget || target.closest("a")) setMenuAt(null);
          }}
          className="m-0 h-full max-h-none w-72 max-w-[85vw] bg-white p-0 shadow-xl backdrop:bg-black/40 md:hidden"
        >
          <div className="flex h-full flex-col">
            <div className="flex h-14 shrink-0 items-center justify-between border-b border-gray-200 px-2">
              <span className="flex items-center gap-2 px-2">
                <Logo size={28} />
                <span className="font-semibold text-brand">{brand}</span>
              </span>
              <button
                type="button"
                onClick={() => setMenuAt(null)}
                className="flex h-11 w-11 items-center justify-center rounded-md text-gray-800 hover:bg-gray-100"
              >
                <span aria-hidden="true" className="text-2xl leading-none">
                  ×
                </span>
                <span className="sr-only">{t("nav.close")}</span>
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-auto [&>nav]:w-full [&>nav]:border-r-0">{nav}</div>
          </div>
        </dialog>
      )}
    </div>
  );
}

function MenuIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width="24"
      height="24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path strokeLinecap="round" d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  );
}

const navLink =
  "flex items-center rounded-md px-3 py-2 text-sm text-gray-800 hover:bg-gray-100 [&.active]:bg-brand [&.active]:text-brand-trace";

function CustomerNav() {
  const { t } = useTranslation();
  const { current } = useOrg();
  return (
    <nav aria-label={t("nav.label")} className="w-52 shrink-0 border-r border-gray-200 bg-white p-3">
      <ul className="space-y-1">
        <li>
          <Link to="/" className={navLink} activeOptions={{ exact: true }}>
            {t("nav.home")}
          </Link>
        </li>
        <li>
          <Link to="/devices" className={navLink}>
            {t("nav.devices")}
          </Link>
        </li>
        {MAP_SCREEN && (
          <li>
            <Link to="/map" className={navLink}>
              {t("nav.map")}
            </Link>
          </li>
        )}
        <li>
          <Link to="/dashboards" className={navLink}>
            {t("nav.dashboards")}
          </Link>
        </li>
        {/* Between Dashboards and Alarms, on every plan (specs/web-automations WAU-01). */}
        <li>
          <Link to="/automations" className={navLink}>
            {t("nav.automations")}
          </Link>
        </li>
        <li>
          <Link to="/alarms" className={`${navLink} flex items-center gap-2`}>
            {t("nav.alarms")}
            <AlarmsBadge />
          </Link>
        </li>
        <li>
          <Link to="/help" className={`${navLink} flex items-center gap-2`}>
            {t("nav.help")}
            <HelpBadge />
          </Link>
        </li>
        {/* Owners and Admins of a partner organisation (partners WEB-PRT-01). */}
        {hasPartnerPages(current) && (
          <>
            <li>
              <Link to="/customers" className={navLink}>
                {t("nav.customers")}
              </Link>
            </li>
            <li>
              <Link to="/stock" className={navLink}>
                {t("nav.stock")}
              </Link>
            </li>
          </>
        )}
        {/* Owners and Admins of a business organisation (organisation-admin WEB-ORG-01, -02). */}
        {hasOrganisationSection(current) && (
          <li>
            <Link to="/organisation" className={navLink}>
              {t("nav.organisation")}
            </Link>
          </li>
        )}
      </ul>
    </nav>
  );
}

/** The signed-in customer app: loads /v1/me, picks the organisation, then shows the routes inside it. */
/** The live socket for the organisation acting, started again when the user switches organisation. */
function OrgLive({ children }: { children: ReactNode }) {
  const { orgId, current } = useOrg();
  // The street map of the organisation's region (map-tiles MAP-10, MAP-11): switching organisation switches it.
  return (
    <LiveProvider key={orgId} orgId={orgId}>
      <MapStyleProvider dataRegion={current?.data_region}>{children}</MapStyleProvider>
    </LiveProvider>
  );
}

export function AppShell() {
  const { t, i18n } = useTranslation();
  const me = useQuery(meQuery());
  // The server's role for the development bar (server-role SR-36); the bar reads it from the cache.
  useQuery(serverRoleQuery("customers"));

  // The account's locale applies unless the user chose a language on this browser (WEB-F-SH-04).
  const locale = me.data?.locale;
  useEffect(() => {
    if (!chosenLanguage() && isLanguage(locale) && i18n.language !== locale) void i18n.changeLanguage(locale);
  }, [locale, i18n]);

  if (me.isPending) return <Loading />;
  if (me.isError) return <ErrorMessage error={me.error} onRetry={() => void me.refetch()} />;

  const user = me.data;
  return (
    <OrgProvider key={user.id} userId={user.id} memberships={user.memberships}>
      <OrgLive>
        <Frame
          brand={t("app.name")}
          actions={
            <>
              <OrgSwitcher />
              <LanguageSwitch />
              <UserMenu
                name={user.display_name ?? user.email}
                servers={showServers(user.developer, maybeConfig()?.environment)}
                notifications
              />
            </>
          }
          nav={<CustomerNav />}
          phone
        >
          {user.memberships.length === 0 ? (
            <section className="p-6">
              <h1 className="text-lg font-semibold text-brand">{t("devices.title")}</h1>
              <FirstDevice />
            </section>
          ) : (
            <Outlet />
          )}
        </Frame>
      </OrgLive>
    </OrgProvider>
  );
}
